import type { ElasticQueryFilters } from '@src/models/interfaces/general/ElasticQueryParams.interface';
import type {
  ElasticSearchHit,
  ElasticSearchResponse,
} from '@src/models/interfaces/general/ElasticSearchResponseInterface';
import type {
  ShiftElasticSearchIdSource,
  ShiftListElasticFiltersParams,
  ShiftSearchIdsResult,
} from '@src/models/interfaces/shift.interface';
import { SHIFT_TYPES_OPTIONS } from '@src/packages/shifts/constants';
import { buildPartialMatch } from '@src/util/elastic/buildPartialMatch';
import { escapeQueryStringValue } from '@src/util/elastic/escapeQueryStringValue';

export class ShiftMapper {
  static toListElasticFilters({
    searchTerm = '',
    exactMatch = false,
    searchFields = ['name', 'regulationName'],
    shiftType = '',
    exceptionalShiftType = SHIFT_TYPES_OPTIONS.EXCEPTIONAL,
    exceptional = null,
  }: ShiftListElasticFiltersParams = {}): ElasticQueryFilters {
    const filters: ElasticQueryFilters = {};
    const trimmedSearch = searchTerm.trim();
    const fields = searchFields.length > 0 ? searchFields : ['name'];

    if (trimmedSearch) {
      const escapedSearch = escapeQueryStringValue(trimmedSearch);
      filters.textSearch = exactMatch
        ? `(${fields.map(field => `${field}:"${escapedSearch}"`).join(' OR ')})`
        : buildPartialMatch(fields, trimmedSearch);
    }

    if (shiftType && shiftType !== exceptionalShiftType) {
      filters.term = {
        ...(filters.term ?? {}),
        type: shiftType,
      };
    }

    if (shiftType === exceptionalShiftType && typeof exceptional !== 'boolean') {
      filters.term = {
        ...(filters.term ?? {}),
        hasPolicies: true,
      };
    }

    if (typeof exceptional === 'boolean') {
      filters.term = {
        ...(filters.term ?? {}),
        hasPolicies: exceptional,
      };
    }

    return filters;
  }

  static fromElasticSearchIdHit = (
    hit: ElasticSearchHit<ShiftElasticSearchIdSource>,
  ): string | null => {
    const id = hit._source?.id ?? hit._id;
    return id || null;
  };

  static fromElasticSearchIdsResponse = (
    response: ElasticSearchResponse<ShiftElasticSearchIdSource> | null,
  ): ShiftSearchIdsResult => {
    const hits = response?.hits?.hits ?? [];
    const ids = hits
      .map(hit => ShiftMapper.fromElasticSearchIdHit(hit))
      .filter((id): id is string => id != null);

    return {
      ids,
      total: response?.hits?.total?.value ?? ids.length,
    };
  };
}

export default ShiftMapper;
